import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Alert, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";

import { db } from "../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../shared/auth/AuthProvider";
import { AFFILIATE_COLLECTIONS } from "../../../shared/utils/affiliateSchema";

const PAYOUT_STATUS_OPTIONS = [
  { value: "all", label: "ทุกสถานะ" },
  { value: "unpaid", label: "ยังไม่จ่าย" },
  { value: "paid", label: "จ่ายแล้ว" },
];

function getCurrentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatBaht(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

function toDate(value) {
  try {
    const d =
      value?.toDate?.() ||
      (value instanceof Date ? value : value ? new Date(value) : null);
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

function formatThaiDate(value) {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getMonthKey(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function AffiliateEarningsPage() {
  const { user } = useAuth();
  const uid = user?.uid || "";

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey);
  const [courseId, setCourseId] = useState("all");
  const [payoutStatus, setPayoutStatus] = useState("all");

  useEffect(() => {
    if (!uid) return;
    const loadData = async () => {
      try {
        setLoading(true);
        setError("");
        const snap = await getDocs(
          query(
            collection(db, AFFILIATE_COLLECTIONS.commissions),
            where("affiliateUserId", "==", uid)
          )
        );
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          const ta = toDate(a?.createdAt)?.getTime() || 0;
          const tb = toDate(b?.createdAt)?.getTime() || 0;
          return tb - ta;
        });
        setItems(rows);
      } catch (e) {
        console.error(e);
        setError(e?.message || "โหลดรายได้ Affiliate ไม่สำเร็จ");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [uid]);

  const courseOptions = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const id = item?.courseId || "";
      if (!id || map.has(id)) return;
      map.set(id, item?.courseTitle || id);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (monthKey && getMonthKey(item?.createdAt) !== monthKey) return false;
      if (courseId !== "all" && item?.courseId !== courseId) return false;
      if (payoutStatus !== "all" && item?.payoutStatus !== payoutStatus) return false;
      return true;
    });
  }, [items, monthKey, courseId, payoutStatus]);

  const totalCommission = useMemo(
    () => filteredItems.reduce((sum, item) => sum + Number(item?.commissionAmount || 0), 0),
    [filteredItems]
  );

  if (loading) {
    return (
      <div className="py-4 text-center text-muted">
        <Spinner animation="border" size="sm" /> กำลังโหลดรายได้...
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-1">Affiliate Earnings</h4>
      <div className="text-muted small mb-1">*คาดว่าจะได้รับภายในวันที่ 30</div>
      <div className="text-muted small mb-3">รายการค่าคอมมิชชันจากยอดขายที่ชำระสำเร็จ</div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Card className="border mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label>เดือน</Form.Label>
              <Form.Control
                type="month"
                value={monthKey}
                onChange={(e) => setMonthKey(e.target.value)}
              />
              <Button
                type="button"
                variant="link"
                size="sm"
                className="px-0 mt-1 text-decoration-none"
                onClick={() => setMonthKey("")}
              >
                แสดงทั้งหมด
              </Button>
            </Col>
            <Col md={4}>
              <Form.Label>คอร์ส</Form.Label>
              <Form.Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="all">ทุกคอร์ส</option>
                {courseOptions.map(([id, title]) => (
                  <option key={id} value={id}>
                    {title}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label>สถานะจ่ายเงิน</Form.Label>
              <Form.Select value={payoutStatus} onChange={(e) => setPayoutStatus(e.target.value)}>
                {PAYOUT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border mb-3">
        <Card.Body>
          <div className="text-muted small">ค่าคอมมิชชันรวมตามตัวกรอง</div>
          <div className="h4 mb-0">{formatBaht(totalCommission)}</div>
        </Card.Body>
      </Card>

      {filteredItems.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center text-muted py-5">ยังไม่มีรายการค่าคอมมิชชัน</Card.Body>
        </Card>
      ) : (
        <Table responsive bordered hover>
          <thead>
            <tr>
              <th>วันที่สำเร็จ</th>
              <th>คอร์ส</th>
              <th>แพ็กเกจ</th>
              <th className="text-end">ยอดขาย</th>
              <th className="text-end">%</th>
              <th className="text-end">ค่าคอมมิชชัน</th>
              <th>สถานะจ่ายเงิน</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => (
              <tr key={item.id}>
                <td>{formatThaiDate(item.createdAt)}</td>
                <td>{item.courseTitle || item.courseId || "-"}</td>
                <td>{item.coursePackageLabel || item.coursePackage || "-"}</td>
                <td className="text-end">{formatBaht(item.saleAmount)}</td>
                <td className="text-end">{Number(item.commissionPercent || 0).toLocaleString("th-TH")}%</td>
                <td className="text-end fw-semibold">{formatBaht(item.commissionAmount)}</td>
                <td>{item.payoutStatus || "unpaid"}</td>
                <td>{item.status || "-"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}
